import { isStoreFallbackEnabled } from '@/api/apiMode';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildBookingInvite } from '@/utils/icsInvite';
import { autoSendWaiverOnConfirm } from '@/domain/waiverFlow';
import { customerName } from '@/utils/format';
import { format } from 'date-fns';
import { seedNow } from '@/domain/seedContext';
import { studioDateWords, studioTimeWords } from '@atink/domain';
import type { Booking, Channel } from '@/domain/types';

/**
 * Booking-confirmation side effects (Prompt I3, 09 Jul ICS ruling). When a booking
 * is confirmed we (1) attach a downloadable calendar invite to the customer
 * conversation - accountability / opt-out friction - and (2) run the date-driven
 * waiver dispatch (I2). Kept in its own file so the confirm call sites swap a
 * single import and neither `depositFlow` nor `rescheduleFlow` gains a cycle.
 */

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Walk the keep-original reschedule chain back to its root, returning the root
 * booking id (the stable ICS UID) and the chain depth (the ICS SEQUENCE, bumped
 * on each move so calendars treat the invite as an update).
 */
function chainRootAndSequence(
  booking: Booking,
  bookings: Record<string, Booking>,
): { rootId: string; sequence: number } {
  let cur = booking;
  let sequence = 0;
  const seen = new Set<string>([cur.id]);
  while (cur.rescheduledFromBookingId) {
    const prev = bookings[cur.rescheduledFromBookingId];
    if (!prev || seen.has(prev.id)) break;
    cur = prev;
    sequence += 1;
    seen.add(cur.id);
  }
  return { rootId: cur.id, sequence };
}

/**
 * Append the booking confirmation message + calendar invite to the customer's
 * conversation. Idempotent per booking (a fixed entry id guards re-confirms); a
 * rescheduled booking gets a fresh entry carrying the same chain-root UID with a
 * bumped SEQUENCE. No-op without a customer or studio on record.
 */
export function sendBookingConfirmation(bookingId: string, opts: { inviteOnly?: boolean } = {}): void {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return;
  const customer = brain.customers[booking.customerId];
  const studio = brain.studios[booking.studioId];
  if (!customer || !studio) return;

  const entryId = `convo-invite-${bookingId}`;
  if (customer.conversation.some((e) => e.id === entryId)) return;

  const artist = brain.artists[booking.artistId];
  const { rootId, sequence } = chainRootAndSequence(booking, brain.bookings);
  const ics = buildBookingInvite(booking, customer, artist, studio, {
    uid: `${rootId}@addikted-to-ink`,
    sequence,
    dtstampIso: nowIso(),
  });
  const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;

  // Deliver on the customer's last messaging channel; never a voice/walk-in "send".
  const last = customer.conversation[customer.conversation.length - 1]?.channel;
  const channel: Channel = last && last !== 'voice' && last !== 'walk-in' ? last : 'sms';
  const artistName = artist ? artist.firstName : 'your artist';
  const when = format(new Date(booking.start), 'EEE d MMM, h:mma');

  brain.addConversationEntry(customer.id, {
    id: entryId,
    customerId: customer.id,
    channel,
    direction: 'out',
    body: opts.inviteOnly
      ? `Calendar invite for ${studioDateWords(booking.start)}, ${studioTimeWords(booking.start)} with ${artistName} at ${studio.name}.`
      : `${customerName(customer)}, you are locked in for ${when} with ${artistName} at ${studio.name}. Calendar invite attached.`,
    timestamp: nowIso(),
    authorRole: 'agent',
    authorName: 'You',
    attachments: [{ kind: 'file', url: dataUri }],
  });
}

/**
 * Single choke for "a booking just became confirmed" (I3): dispatch the waiver on
 * the T-4d schedule (I2/L6) and attach the calendar invite. Replaces the bare
 * `autoSendWaiverOnConfirm` calls at every confirm site.
 *
 * B6: a confirm that came from a PAYMENT the server took (pay-deposit, card over
 * the phone, create-and-pay at the desk) is texted by the server — the receipt
 * with the booking details, and the waiver inside T-4d — so the served build only
 * attaches the calendar invite (09 Jul ICS ruling), with no second "locked in" and
 * no second waiver line. The store-fallback demo (no server) keeps sending both.
 * `textedOnServer` is the same for a confirm the server texted without a payment
 * (L1, tv-vbpk: placing a deposit on file texts the confirmation + waiver link).
 */
export function onBookingConfirmed(bookingId: string, opts: { paidOnServer?: boolean; textedOnServer?: boolean } = {}): void {
  if ((opts.paidOnServer || opts.textedOnServer) && !isStoreFallbackEnabled()) {
    sendBookingConfirmation(bookingId, { inviteOnly: true });
    return;
  }
  autoSendWaiverOnConfirm(bookingId);
  sendBookingConfirmation(bookingId);
}
